import { GridColDef } from "@mui/x-data-grid-premium";
import {
  Groups,
  Person,
  Business,
  PriorityHigh,
  Circle,
  SupportAgent,
  HowToReg
} from "@mui/icons-material";
import { Chip, Box, Tooltip } from "@mui/material";
import { useMemo } from "react";
import EventNote from "@mui/icons-material/EventNote";
import { BrokerSummary } from "../../../../../api/Api";
import {
  dateColumn,
  operationalReadinessColumn
} from "../../../../../components/datagrid/overviewCells";

const UNASSIGNED = "Unassigned";

// Distinct Market Access handlers present in the current rows, used as the dropdown
// options for the filter. Rows without a handler collapse to a single "Unassigned"
// option so they can be filtered for explicitly.
const useHandlerOptions = (rows: BrokerSummary[]) =>
  useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      set.add(row.marketAccessHandler || UNASSIGNED);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

export const getPriorityColor = (urgent: boolean | undefined | null) => {
  switch (urgent) {
    case true:
      return {
        backgroundColor: "#D21919",
        color: "#FFF"
      };

    default:
      return {};
  }
};

const normalizeProgress = (
  progress: string | null | undefined
): "Completed" | "In Progress" | "Not Initiated" => {
  switch (progress?.toLowerCase()) {
    case "completed":
      return "Completed";
    case "in progress":
      return "In Progress";
    default:
      return "Not Initiated";
  }
};

const getProgressIcon = (progress: string | null | undefined) => {
  switch (normalizeProgress(progress)) {
    case "Completed":
      return (
        <Circle sx={{ fill: (t) => t.palette.NICE_GREEN }} fontSize="small" />
      );
    case "In Progress":
      return <Circle sx={{ fill: "#529CCF" }} fontSize="small" />;
    case "Not Initiated":
    default:
      return <Circle sx={{ fill: "#C6C6C6" }} fontSize="small" />;
  }
};

const renderProgressCell = (
  status: "Completed" | "In Progress" | "Not Initiated"
) => (
  <Tooltip title={status}>
    <Box
      height="100%"
      display="flex"
      alignItems="center"
      fontSize="12px"
      lineHeight={1}
      gap={0.5}
    >
      {getProgressIcon(status)}
      {status}
    </Box>
  </Tooltip>
);

type SignOffLabel =
  | "Not started"
  | "Draft"
  | "In Progress"
  | "Rejected"
  | "Completed";

// Ordered by where the state sits in the sign-off flow, so sorting the column
// walks the pipeline instead of sorting the labels alphabetically.
const SIGN_OFF_ORDER: SignOffLabel[] = [
  "Not started",
  "Draft",
  "In Progress",
  "Rejected",
  "Completed"
];

// Rows saved before the summary table gained a signOffStatus column come back
// null, and so do brokers that never had a sign-off opened. Both read as
// "Not started".
const normalizeSignOff = (status: string | null | undefined): SignOffLabel => {
  switch (status?.toLowerCase()) {
    case "draft":
      return "Draft";
    case "in_progress":
      return "In Progress";
    case "rejected":
      return "Rejected";
    case "completed":
      return "Completed";
    default:
      return "Not started";
  }
};

const getSignOffIcon = (status: SignOffLabel) => {
  switch (status) {
    case "Completed":
      return (
        <Circle sx={{ fill: (t) => t.palette.NICE_GREEN }} fontSize="small" />
      );
    case "Rejected":
      return <Circle sx={{ fill: "#D21919" }} fontSize="small" />;
    case "In Progress":
      return <Circle sx={{ fill: "#529CCF" }} fontSize="small" />;
    case "Draft":
      return <Circle sx={{ fill: "#9AA0A6" }} fontSize="small" />;
    case "Not started":
    default:
      return <Circle sx={{ fill: "#C6C6C6" }} fontSize="small" />;
  }
};

const renderSignOffCell = (status: SignOffLabel) => (
  <Tooltip title={status}>
    <Box
      height="100%"
      display="flex"
      alignItems="center"
      fontSize="12px"
      lineHeight={1}
      gap={0.5}
    >
      {getSignOffIcon(status)}
      {status}
    </Box>
  </Tooltip>
);

export const useBrokerColumns = (
  rows: BrokerSummary[] = [],
  showMarketAccessHandler = false
): GridColDef<BrokerSummary>[] => {
  const handlerOptions = useHandlerOptions(rows);
  const memoizedColumns = useMemo(() => {
    const columns: GridColDef<BrokerSummary>[] = [
      {
        field: "brokerName",
        headerName: "Broker",
        width: 300,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Business fontSize="small" />
            Broker
          </Box>
        )
      },
      {
        field: "tradingTeam",
        headerName: "Trading Team",
        width: 200,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Groups fontSize="small" />
            Trading Team
          </Box>
        )
      },
      {
        field: "contactPersonTradingTeam",
        headerName: "Requester",
        width: 250,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Person fontSize="small" />
            MFT Contact Person
          </Box>
        )
      },
      dateColumn<BrokerSummary>(
        "requestedAt",
        "Requested",
        (row) => row.requestedAt,
        <EventNote fontSize="small" />
      ),
      // Only the global role (Admin / Market Access) sees who from MA is handling each row.
      ...(showMarketAccessHandler
        ? [
            {
              field: "marketAccessHandler",
              headerName: "MA Handler",
              width: 220,
              sortable: true,
              filterable: true,
              type: "singleSelect",
              valueOptions: handlerOptions,
              valueGetter: (_value, row) =>
                row.marketAccessHandler || UNASSIGNED,
              renderHeader: () => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SupportAgent fontSize="small" />
                  MA Handler
                </Box>
              )
            } as GridColDef<BrokerSummary>
          ]
        : []),
      {
        field: "urgent",
        headerName: "Priority",
        width: 120,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PriorityHigh fontSize="small" />
            Priority
          </Box>
        ),
        renderCell: (params) => {
          return params.value === true ? (
            <Chip
              label={"URGENT"}
              size="small"
              variant="filled"
              sx={{
                ...getPriorityColor(params.value)
              }}
            />
          ) : null;
        }
      },
      operationalReadinessColumn<BrokerSummary>(
        (row) => row.operationalReadinessStatus
      ),
      {
        field: "signOffStatus",
        headerName: "Sign Off",
        width: 150,
        sortable: true,
        filterable: true,
        type: "singleSelect",
        valueOptions: SIGN_OFF_ORDER,
        valueGetter: (_value, row) => normalizeSignOff(row.signOffStatus),
        sortComparator: (a: SignOffLabel, b: SignOffLabel) =>
          SIGN_OFF_ORDER.indexOf(a) - SIGN_OFF_ORDER.indexOf(b),
        renderCell: (params) => renderSignOffCell(params.value),
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HowToReg fontSize="small" />
            Sign Off
          </Box>
        )
      },
      {
        field: "progress_marketAccess",
        headerName: "Market Access",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) =>
          normalizeProgress(row.progress?.marketAccess),
        renderCell: (params) => renderProgressCell(params.value)
      },
      {
        field: "progress_compliance",
        headerName: "Compliance",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) =>
          normalizeProgress(row.progress?.compliance),
        renderCell: (params) => renderProgressCell(params.value)
      },
      {
        field: "progress_creditRisk",
        headerName: "Credit Risk",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) =>
          normalizeProgress(row.progress?.creditRisk),
        renderCell: (params) => renderProgressCell(params.value)
      },
      {
        field: "progress_legal",
        headerName: "Legal",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) => normalizeProgress(row.progress?.legal),
        renderCell: (params) => renderProgressCell(params.value)
      },
      {
        field: "progress_risk",
        headerName: "Risk",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) => normalizeProgress(row.progress?.risk),
        renderCell: (params) => renderProgressCell(params.value)
      },

      {
        field: "progress_finance",
        headerName: "Finance",
        width: 150,
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) => normalizeProgress(row.progress?.finance),
        renderCell: (params) => renderProgressCell(params.value)
      }
    ];
    return columns;
  }, [handlerOptions, showMarketAccessHandler]);

  return memoizedColumns;
};
